import React, { useMemo, useState, useEffect } from 'react';
import { useTeam } from '../../../shared/context/teamContext';
import axiosInstance from '../../../shared/services/api/axiosInstance';
import { ChevronDown, ChevronUp } from 'lucide-react';
import LiquidFillLoader from '../../../shared/components/ui/LiquidFillLoader';

const CipherEvents = () => {
  const { selectedTeam } = useTeam();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedKey, setExpandedKey] = useState(null);
  const [loadingDetailsKey, setLoadingDetailsKey] = useState(null);
  const [detailsByKey, setDetailsByKey] = useState({});

  useEffect(() => {
    if (selectedTeam?._id) {
      fetchEventSummaries();
    }
  }, [selectedTeam]);

  const fetchEventSummaries = async () => {
    if (!selectedTeam?._id) return;
    
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get('/events/summaries', {
        params: {
          team_id: selectedTeam._id
        }
      });

      if (response.data.success) {
        setEvents(response.data.summaries || []);
      } else {
        setError('Failed to fetch events');
      }
    } catch (err) {
      console.error('Error fetching event summaries:', err);
      setError(err.response?.data?.error || 'Failed to fetch events');
    } finally {
      setLoading(false);
    }
  };

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      const date = new Date(timestamp);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);
      const diffMonths = Math.floor(diffDays / 30);
      const diffYears = Math.floor(diffDays / 365);

      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
      if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
      if (diffDays < 30) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
      if (diffMonths < 12) return `${diffMonths} month${diffMonths > 1 ? 's' : ''} ago`;
      return `${diffYears} year${diffYears > 1 ? 's' : ''} ago`;
    } catch {
      return 'N/A';
    }
  };

  const stableEventKey = (event) => `${event.event_type}:${event.event_name}`;

  const parseCustomPropertiesKeys = (customProperties) => {
    if (!customProperties) return [];
    if (typeof customProperties === 'object') {
      return Object.keys(customProperties);
    }
    if (typeof customProperties === 'string') {
      try {
        const parsed = JSON.parse(customProperties);
        if (parsed && typeof parsed === 'object') return Object.keys(parsed);
      } catch (_) {
        return [];
      }
    }
    return [];
  };

  const getDetectedPropertyKeys = (evt) => {
    if (!evt || typeof evt !== 'object') return [];
    const exclude = new Set([
      // identifiers / DB fields
      'id', 'team_id', 'website_id', 'session_db_id', 'created_at', 'updated_at',
      // we show these elsewhere / not needed as “properties list”
      'event_name', 'event_type', 'event_category', 'timestamp',
      // large / internal payloads (we only list keys, not values)
      'custom_properties', 'auto_event_data', 'element_position'
    ]);

    return Object.keys(evt)
      .filter((k) => !exclude.has(k))
      .filter((k) => {
        const v = evt[k];
        if (v === null || v === undefined) return false;
        if (typeof v === 'string' && v.trim() === '') return false;
        if (Array.isArray(v) && v.length === 0) return false;
        if (typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length === 0) return false;
        return true;
      })
      .sort((a, b) => a.localeCompare(b));
  };

  const ensureDetailsLoaded = async (event) => {
    const key = stableEventKey(event);
    if (detailsByKey[key]) return;
    if (!selectedTeam?._id) return;

    setLoadingDetailsKey(key);
    try {
      // Fetch a sample event row for this event name/type
      const response = await axiosInstance.get('/events', {
        params: {
          team_id: selectedTeam._id,
          search: event.event_name,
          limit: 1
        }
      });

      const sampleEvent = response.data?.events?.[0];
      if (!response.data?.success || !sampleEvent?.id) {
        setDetailsByKey((prev) => ({
          ...prev,
          [key]: { detectedKeys: [], customKeys: [], error: 'No sample event found' }
        }));
        return;
      }

      // Fetch full event details for richer properties (custom_properties, etc.)
      const detailResponse = await axiosInstance.get(`/events/${sampleEvent.id}`, {
        params: { team_id: selectedTeam._id }
      });

      const fullEvent = detailResponse.data?.event || sampleEvent;
      const detectedKeys = getDetectedPropertyKeys(fullEvent);
      const customKeys = fullEvent?.event_type === 'custom'
        ? parseCustomPropertiesKeys(fullEvent.custom_properties)
        : [];

      setDetailsByKey((prev) => ({
        ...prev,
        [key]: { detectedKeys, customKeys, error: null }
      }));
    } catch (err) {
      console.error('Error fetching event details:', err);
      setDetailsByKey((prev) => ({
        ...prev,
        [key]: { detectedKeys: [], customKeys: [], error: 'Failed to fetch event details' }
      }));
    } finally {
      setLoadingDetailsKey(null);
    }
  };

  const toggleExpand = async (event) => {
    const key = stableEventKey(event);
    const next = expandedKey === key ? null : key;
    setExpandedKey(next);
    if (next) {
      await ensureDetailsLoaded(event);
    }
  };

  const currentExpanded = useMemo(() => {
    if (!expandedKey) return null;
    return detailsByKey[expandedKey] || null;
  }, [detailsByKey, expandedKey]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <LiquidFillLoader size={120} />
        <span className="mt-4 text-sm text-[#475569]" style={{ fontFamily: 'Raleway, sans-serif' }}>
          Loading events...
        </span>
      </div>
    );
  }

  if (error && events.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-red-500" style={{ fontFamily: 'Raleway, sans-serif' }}>
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-800 rounded-xl">
          {error}
        </div>
      )}

      <div className="bg-white border border-gray-200 shadow-sm rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-[#F9FAFB]">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#475569] uppercase tracking-wider">
                  Event Name
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#475569] uppercase tracking-wider">
                  First Occurrence
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#475569] uppercase tracking-wider">
                  Last Occurrence
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#475569] uppercase tracking-wider">
                  Count
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {events.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-4 py-12 text-center text-sm text-[#475569]">
                    No events found.
                  </td>
                </tr>
              ) : (
                events.map((event, index) => {
                  const key = stableEventKey(event);
                  const isExpanded = expandedKey === key;
                  const rowDetails = detailsByKey[key];
                  const isLoadingRow = loadingDetailsKey === key;

                  return (
                    <React.Fragment key={`${key}-${index}`}>
                      <tr
                        className="hover:bg-[#F9FAFB] transition-colors duration-150 cursor-pointer"
                        onClick={() => toggleExpand(event)}
                      >
                        <td className="px-4 py-4 text-sm font-medium text-[#1F2937]">
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              className="text-[#475569] hover:text-[#475569] transition-colors"
                              aria-label={isExpanded ? 'Collapse event details' : 'Expand event details'}
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleExpand(event);
                              }}
                            >
                              {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                            </button>
                            <span>
                              {event.event_type === 'auto' ? (
                                <>
                                  {event.event_name}{' '}
                                  <span className="text-[#475569] font-normal">[AUTO]</span>
                                </>
                              ) : (
                                event.event_name
                              )}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-4 text-sm text-[#475569]">
                          {formatTimeAgo(event.first_occurrence)}
                        </td>
                        <td className="px-4 py-4 text-sm text-[#475569]">
                          {formatTimeAgo(event.last_occurrence)}
                        </td>
                        <td className="px-4 py-4 text-sm text-[#475569]">
                          {event.count.toLocaleString()}
                        </td>
                      </tr>

                      {isExpanded && (
                        <tr className="bg-[#F9FAFB]">
                          <td colSpan={4} className="px-4 py-4">
                            <div className="bg-white border border-gray-200 rounded-2xl p-4">
                              {isLoadingRow ? (
                                <div className="text-sm text-[#475569]" style={{ fontFamily: 'Raleway, sans-serif' }}>
                                  Loading properties...
                                </div>
                              ) : rowDetails?.error ? (
                                <div className="text-sm text-red-600" style={{ fontFamily: 'Raleway, sans-serif' }}>
                                  {rowDetails.error}
                                </div>
                              ) : (
                                <div className="space-y-4">
                                  <div>
                                    <div className="text-xs font-semibold text-[#475569] uppercase tracking-wider mb-2">
                                      Detected properties
                                    </div>
                                    {rowDetails?.detectedKeys?.length ? (
                                      <div className="flex flex-wrap gap-2">
                                        {rowDetails.detectedKeys.map((k) => (
                                          <span
                                            key={k}
                                            className="px-2 py-1 text-xs bg-gray-100 text-[#0f172a] rounded-full border border-gray-200"
                                            style={{ fontFamily: 'Raleway, sans-serif' }}
                                          >
                                            {k}
                                          </span>
                                        ))}
                                      </div>
                                    ) : (
                                      <div className="text-sm text-[#475569]">No detected properties.</div>
                                    )}
                                  </div>

                                  {event.event_type === 'custom' && (
                                    <div>
                                      <div className="text-xs font-semibold text-[#475569] uppercase tracking-wider mb-2">
                                        Custom properties (keys)
                                      </div>
                                      {rowDetails?.customKeys?.length ? (
                                        <div className="flex flex-wrap gap-2">
                                          {rowDetails.customKeys.map((k) => (
                                            <span
                                              key={k}
                                              className="px-2 py-1 text-xs bg-indigo-50 text-indigo-900 rounded-full border border-indigo-100"
                                              style={{ fontFamily: 'Raleway, sans-serif' }}
                                            >
                                              {k}
                                            </span>
                                          ))}
                                        </div>
                                      ) : (
                                        <div className="text-sm text-[#475569]">No custom properties.</div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default CipherEvents;
